import React, { useEffect } from 'react'
import * as ROUTES from 'constants/routes'
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Icon,
  Loading,
  Message,
  Text,
  Link,
} from '@workwhile/ui'
import { ExternalLinkIcon } from 'lucide-react'
import { differenceInMinutes } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { WILL_OR_HAS_WORKED_STATUSES } from 'lib/work'
import { IndeedFlexShiftIssueActions } from 'pages/IndeedFlex/shared/components/IndeedFlexShiftIssueActions'
import { ViewIndeedFlexTimesheetButton } from 'pages/IndeedFlex/shared/components/ViewIndeedFlexTimesheetButton'
import { getIndeedFlexShiftAnchorId } from 'pages/IndeedFlex/shared/utils/jobShifts'
import { useIndeedFlexJobShiftsContextValue } from '../context/IndeedFlexJobShiftsProvider'

const INDEED_FLEX_AGENCY_BASE_URL = 'https://agency.indeedflex.com'

type IndeedFlexJobShiftItem = NonNullable<
  ReturnType<typeof useIndeedFlexJobShiftsContextValue>['state']['data']
>['items'][number]

type IndeedFlexJobShiftWorker = IndeedFlexJobShiftItem['workers'][number]

function buildIndeedFlexJobUrl({
  ifPlatformId,
  ifJobId,
  ifRoleId,
  ifVenueId,
}: {
  ifPlatformId: number
  ifJobId: number
  ifRoleId: number
  ifVenueId: number
}): string {
  const params = new URLSearchParams({
    roleId: ifRoleId.toString(),
    venueId: ifVenueId.toString(),
  })

  return `${INDEED_FLEX_AGENCY_BASE_URL}/platforms/${ifPlatformId}/job-details/${ifJobId}?${params.toString()}`
}

function formatShiftTime(isoTime: string, timezone: string) {
  return formatInTimeZone(isoTime, timezone, 'EEEE, MMM d, h:mm a z')
}

function getIndeedFlexShiftHeadingStatus(
  shift: IndeedFlexJobShiftItem
): string {
  if (shift.associationType === 'ignored') {
    return ' (ignored)'
  }
  if (shift.associationType === 'canceled' || shift.workShiftCancelled) {
    return ' (cancelled)'
  }
  return ''
}

function formatOptionalTime(isoTime: string | null, timezone: string) {
  if (isoTime === null) {
    return '-'
  }
  return formatInTimeZone(isoTime, timezone, 'MMM d, h:mm a')
}

function formatOptionalClockDuration(
  startTime: string | null,
  endTime: string | null
) {
  if (startTime === null || endTime === null) {
    return ''
  }

  const minutes = differenceInMinutes(new Date(endTime), new Date(startTime))
  if (!Number.isFinite(minutes) || minutes < 0) {
    return ''
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (hours === 0) {
    return ` · ${remainingMinutes}m`
  }
  if (remainingMinutes === 0) {
    return ` · ${hours}h`
  }
  return ` · ${hours}h ${remainingMinutes}m`
}

function formatBreakMinutes(minutes: number | null) {
  if (minutes === null) {
    return '-'
  }
  return `${minutes} min`
}

function formatBookingType(worker: IndeedFlexJobShiftWorker) {
  const bookingTypeLabel =
    worker.ifBookingType === null ? 'WW only' : worker.ifBookingType

  if (worker.ifNoShowReasonId === null) {
    return bookingTypeLabel
  }

  return worker.ifNoShowReasonDescription || worker.ifNoShowReasonId
}

function areOptionalIsoTimesEqual(
  left: string | null,
  right: string | null
): boolean {
  if (left === null && right === null) {
    return true
  }
  if (left === null || right === null) {
    return false
  }

  const leftMs = Date.parse(left)
  const rightMs = Date.parse(right)
  if (Number.isNaN(leftMs) || Number.isNaN(rightMs)) {
    return left === right
  }

  // Display is minute-precision; ignore second/ms drift.
  return Math.floor(leftMs / 60_000) === Math.floor(rightMs / 60_000)
}

function isStatusBookingTypeMismatch(worker: IndeedFlexJobShiftWorker) {
  const { ifBookingType, ifNoShowReasonId, shouldBeBailed, wwStatus } = worker

  if (ifBookingType === 'indeed_flex_only') {
    return true
  }

  if (wwStatus === 'bailed') {
    return shouldBeBailed !== (wwStatus === 'bailed')
  }

  const wwWillOrHasWorked =
    wwStatus !== null && WILL_OR_HAS_WORKED_STATUSES.includes(wwStatus)

  if (ifNoShowReasonId !== null) {
    return wwWillOrHasWorked
  }

  if (ifBookingType === 'scheduled') {
    return !wwWillOrHasWorked
  }
  if (ifBookingType === null || ifBookingType === 'removed') {
    return wwWillOrHasWorked
  }

  const _exhaustive: never = ifBookingType
  return _exhaustive
}

function shouldListWorker(worker: IndeedFlexJobShiftWorker) {
  const wwWillOrHasWorked =
    worker.wwStatus !== null &&
    WILL_OR_HAS_WORKED_STATUSES.includes(worker.wwStatus)

  if (wwWillOrHasWorked) {
    return true
  }

  // Inactive/missing WW status with no IF booking or a removed booking is noise.
  return (
    worker.ifBookingType !== null &&
    ['scheduled', 'indeed_flex_only'].includes(worker.ifBookingType)
  )
}

function getWorkerClocksInconsistencies(worker: IndeedFlexJobShiftWorker) {
  const hasWw = worker.associationId !== null
  const hasIf = worker.ifBookingType !== null
  const statusMismatch = isStatusBookingTypeMismatch(worker)
  const timesMismatch =
    (!areOptionalIsoTimesEqual(worker.wwStartedAt, worker.ifClockInTime) ||
      !areOptionalIsoTimesEqual(worker.wwCompletedAt, worker.ifClockOutTime)) &&
    worker.ifNoShowReasonId === null

  if (!hasWw || !hasIf) {
    return {
      statusMismatch,
      timesMismatch: false,
      breakMismatch: false,
    }
  }

  return {
    statusMismatch,
    timesMismatch,
    breakMismatch:
      (worker.wwBreakDurationMinutes ?? 0) >
      (worker.ifBreakDurationMinutes ?? 0),
  }
}

const DetailRow = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => {
  return (
    <Flex gap={2} alignItems="baseline">
      <Text fontSize={1} color="lightText" style={{ minWidth: 72 }}>
        {label}
      </Text>
      <Box flex={1}>{children}</Box>
    </Flex>
  )
}

const NamedId = ({ name, id }: { name: string | null; id: number | null }) => {
  if (name === null || id === null) {
    return <Text>-</Text>
  }

  return (
    <Text>
      {name} ({id})
    </Text>
  )
}

const CompanyName = ({
  companyId,
  companyName,
}: {
  companyId: number | null
  companyName: string | null
}) => {
  if (companyId === null || companyName === null) {
    return <Text>-</Text>
  }

  return (
    <Text>
      {companyName} (
      <Link
        to={ROUTES.COMPANY_DETAIL.replace(':companyId', companyId.toString())}
        target="_blank"
      >
        {companyId}
      </Link>
      )
    </Text>
  )
}

const OpenIndeedFlexJobButton = ({
  ifClientId,
  ifJobId,
  ifRoleId,
  ifVenueId,
}: {
  ifClientId: number
  ifJobId: number
  ifRoleId: number
  ifVenueId: number
}) => {
  const {
    state: { clients },
  } = useIndeedFlexJobShiftsContextValue()

  const ifPlatformId = clients.find(
    (client) => client.ifClientId === ifClientId
  )?.ifPlatformId

  if (ifPlatformId === undefined) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buildIndeedFlexJobUrl({
        ifPlatformId,
        ifJobId,
        ifRoleId,
        ifVenueId,
      })}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Open IF job</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const OpenWorkWhileShiftButton = ({
  workShiftId,
}: {
  workShiftId: number | null
}) => {
  if (workShiftId === null) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={ROUTES.SHIFT_DETAIL.replace(':shiftId', workShiftId.toString())}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">View shift</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const WorkerName = ({ worker }: { worker: IndeedFlexJobShiftWorker }) => {
  if (worker.workerId === null) {
    return <Text>{worker.workerName ?? '-'}</Text>
  }

  return (
    <Text>
      {worker.workerName ?? 'Unknown'} (
      <Link
        to={ROUTES.USERS.replace(':keyword', worker.workerId.toString())}
        target="_blank"
      >
        {worker.workerId}
      </Link>
      )
    </Text>
  )
}

const WorkersTable = ({
  workers,
  timezone,
}: {
  workers: IndeedFlexJobShiftWorker[]
  timezone: string
}) => {
  const listedWorkers = workers.filter(shouldListWorker)

  if (listedWorkers.length === 0) {
    return <Text color="lightText">No workers on this shift.</Text>
  }

  return (
    <Box style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'left',
                padding: '4px 8px 4px 0',
                width: '40%',
              }}
            >
              <Text fontWeight="bold" fontSize={1}>
                Worker
              </Text>
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '4px 8px',
                width: '30%',
              }}
            >
              <Text fontWeight="bold" fontSize={1}>
                WW
              </Text>
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '4px 8px',
                width: '30%',
              }}
            >
              <Text fontWeight="bold" fontSize={1}>
                Indeed Flex
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {listedWorkers.map((worker) => {
            const { statusMismatch, timesMismatch, breakMismatch } =
              getWorkerClocksInconsistencies(worker)
            const statusColor = statusMismatch ? 'warning' : undefined
            const timesColor = timesMismatch ? 'warning' : 'lightText'
            const breakColor = breakMismatch ? 'warning' : 'lightText'

            return (
              <tr
                key={
                  worker.associationId ??
                  `${worker.workerId ?? 'unknown'}-${worker.ifWorkerId ?? 'none'}`
                }
                style={{ borderTop: '1px solid #e8e8e8' }}
              >
                <td style={{ padding: '8px 8px 8px 0', verticalAlign: 'top' }}>
                  <WorkerName worker={worker} />
                  {worker.ifWorkerId !== null ? (
                    <Text fontSize={1} color="lightText">
                      IF worker {worker.ifWorkerId}
                    </Text>
                  ) : null}
                </td>
                <td style={{ padding: '8px', verticalAlign: 'top' }}>
                  <Text
                    fontSize={1}
                    color={statusColor}
                    fontWeight={statusMismatch ? 'bold' : undefined}
                  >
                    {worker.wwStatus ?? '-'}
                    {worker.associationId !== null
                      ? ` · assoc ${worker.associationId}`
                      : ''}
                  </Text>
                  <Text
                    fontSize={1}
                    color={timesColor}
                    fontWeight={timesMismatch ? 'bold' : undefined}
                  >
                    {formatOptionalTime(worker.wwStartedAt, timezone)}
                    {' → '}
                    {formatOptionalTime(worker.wwCompletedAt, timezone)}
                    {formatOptionalClockDuration(
                      worker.wwStartedAt,
                      worker.wwCompletedAt
                    )}
                  </Text>
                  <Text
                    fontSize={1}
                    color={breakColor}
                    fontWeight={breakMismatch ? 'bold' : undefined}
                  >
                    Break {formatBreakMinutes(worker.wwBreakDurationMinutes)}
                  </Text>
                </td>
                <td style={{ padding: '8px', verticalAlign: 'top' }}>
                  <Text
                    fontSize={1}
                    color={statusColor}
                    fontWeight={statusMismatch ? 'bold' : undefined}
                  >
                    {formatBookingType(worker)}
                  </Text>
                  <Text
                    fontSize={1}
                    color={timesColor}
                    fontWeight={timesMismatch ? 'bold' : undefined}
                  >
                    {formatOptionalTime(worker.ifClockInTime, timezone)}
                    {' → '}
                    {formatOptionalTime(worker.ifClockOutTime, timezone)}
                    {formatOptionalClockDuration(
                      worker.ifClockInTime,
                      worker.ifClockOutTime
                    )}
                  </Text>
                  <Text
                    fontSize={1}
                    color={breakColor}
                    fontWeight={breakMismatch ? 'bold' : undefined}
                  >
                    Break {formatBreakMinutes(worker.ifBreakDurationMinutes)}
                  </Text>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Box>
  )
}

const IndeedFlexJobShiftCard = ({
  shift,
}: {
  shift: IndeedFlexJobShiftItem
}) => {
  return (
    <Box
      id={getIndeedFlexShiftAnchorId(shift.ifShiftId)}
      style={{ scrollMarginTop: '1rem' }}
    >
      <Card p={3}>
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          gap={3}
          mb={3}
          flexWrap="wrap"
        >
          <Box>
            <Heading level={4} my={0}>
              Shift {shift.ifShiftId}
              {getIndeedFlexShiftHeadingStatus(shift)}
            </Heading>
            <Text mt={1}>
              {formatShiftTime(shift.startTime, shift.timezone)}
              {' – '}
              {formatShiftTime(shift.endTime, shift.timezone)}
            </Text>
            <Text fontSize={1} color="lightText" mt={1}>
              Workers needed {shift.allocationWithoutBuffer} · Cushion workers{' '}
              {shift.reportedDemandBuffer} · Association {shift.associationType}{' '}
              · {shift.workers.length} worker
              {shift.workers.length === 1 ? '' : 's'}
            </Text>
          </Box>
          <Flex gap={2} flexWrap="wrap" alignItems="flex-start">
            <OpenIndeedFlexJobButton
              ifClientId={shift.ifClientId}
              ifJobId={shift.ifJobId}
              ifRoleId={shift.ifRoleId}
              ifVenueId={shift.ifVenueId}
            />
            <ViewIndeedFlexTimesheetButton
              ifJobId={shift.ifJobId}
              startTime={shift.startTime}
              endTime={shift.endTime}
            />
            <OpenWorkWhileShiftButton workShiftId={shift.workShiftId} />
            <IndeedFlexShiftIssueActions shift={shift} />
          </Flex>
        </Flex>

        <Flex gap={4} flexWrap="wrap">
          <Box flex={1} style={{ minWidth: 240 }}>
            <Text fontWeight="bold" mb={2}>
              Indeed Flex
            </Text>
            <Flex flexDirection="column" gap={1}>
              <DetailRow label="Client">
                <Text>
                  {shift.clientName} ({shift.ifClientId})
                </Text>
              </DetailRow>
              <DetailRow label="Job">
                <Text>{shift.ifJobId}</Text>
              </DetailRow>
              <DetailRow label="Role">
                <Text>
                  {shift.roleName ?? '-'} ({shift.ifRoleId})
                </Text>
              </DetailRow>
              <DetailRow label="Venue">
                <Text>
                  {shift.venueName} ({shift.ifVenueId})
                </Text>
              </DetailRow>
            </Flex>
          </Box>

          <Box flex={1} style={{ minWidth: 240 }}>
            <Text fontWeight="bold" mb={2}>
              WorkWhile
            </Text>
            <Flex flexDirection="column" gap={1}>
              <DetailRow label="Company">
                <CompanyName
                  companyId={shift.companyId}
                  companyName={shift.companyName}
                />
              </DetailRow>
              <DetailRow label="Location">
                <NamedId
                  name={shift.companyLocationName}
                  id={shift.companyLocationId}
                />
              </DetailRow>
              <DetailRow label="Position">
                <NamedId
                  name={shift.companyPositionName}
                  id={shift.companyPositionId}
                />
              </DetailRow>
              <DetailRow label="LTA">
                <NamedId name={shift.assignmentName} id={shift.assignmentId} />
              </DetailRow>
              <DetailRow label="Shift">
                {shift.workShiftId === null ? (
                  <Text>-</Text>
                ) : (
                  <Text>
                    <Link
                      to={ROUTES.SHIFT_DETAIL.replace(
                        ':shiftId',
                        shift.workShiftId.toString()
                      )}
                      target="_blank"
                    >
                      {shift.workShiftId}
                    </Link>
                    {shift.workShiftCancelled ? ' (cancelled)' : ''}
                  </Text>
                )}
              </DetailRow>
            </Flex>
          </Box>
        </Flex>

        <Box mt={3}>
          <Text fontWeight="bold" mb={2}>
            Issues
          </Text>
          {shift.issues.length === 0 ? (
            <Text color="lightText">No issues</Text>
          ) : (
            <Text>{shift.issues.join(', ')}</Text>
          )}
        </Box>

        <Box mt={3}>
          <Text fontWeight="bold" mb={2}>
            Workers
          </Text>
          <WorkersTable workers={shift.workers} timezone={shift.timezone} />
        </Box>
      </Card>
    </Box>
  )
}

export const IndeedFlexJobShiftsCards = () => {
  const {
    state: { data, isFetching, isPending, isError, error, hasSearchParams },
  } = useIndeedFlexJobShiftsContextValue()

  useEffect(() => {
    if (!data || !window.location.hash) {
      return
    }
    const targetId = window.location.hash.slice(1)
    const element = document.getElementById(targetId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [data])

  if (!hasSearchParams) {
    return (
      <Message variant="info" borderRadius="small">
        Enter an Indeed Flex job ID to view associated shifts.
      </Message>
    )
  }

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" borderRadius="small">
        {error ||
          'Sorry, something went wrong. Please reach out to eng support if this persists.'}
      </Message>
    )
  }

  if (!data) {
    return null
  }

  if (data.items.length === 0) {
    return (
      <Flex
        mb={2}
        flexDirection="column"
        alignItems="center"
        color="lightText"
        height={50}
        justifyContent="center"
      >
        {isFetching ? <Loading /> : <Text>No shifts found for this job.</Text>}
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column" gap={3}>
      {data.items.map((shift) => (
        <IndeedFlexJobShiftCard key={shift.ifShiftId} shift={shift} />
      ))}
    </Flex>
  )
}
